import {
  AfterContentInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Host,
  Input,
  OnInit,
  Optional,
  Output,
  SkipSelf,
  ViewChild
} from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DateModel } from 'src/app/models/inputs/date-model';
import { UtilityService } from 'src/app/services/utility.service';
import { FormControlBase } from 'src/app/shared/inputs/form-control-base';
import { inputMgsErrs } from 'src/app/validators/message-validation';

@Component({
  selector: 'app-input-text-date',
  templateUrl: './input-text-date.component.html',
  styleUrls: ['./input-text-date.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputTextDateComponent),
      multi: true
    }
  ]
})
export class InputTextDateComponent extends FormControlBase<DateModel> implements OnInit, AfterContentInit {

  /* for input */
  @Input() id;
  @Input() label;
  @Input() showDateDiff = false;
  @Input() isDisabled: boolean;
  @Output() dateChanged: EventEmitter<DateModel> = new EventEmitter();

  /* for style */
  @Input() groupStyleClass = 'mb-3';
  @Input() labelStyleClass = 'mb-1';

  /* View */
  @ViewChild('inpDay') inpDay: ElementRef;
  @ViewChild('inpMonth') inpMonth: ElementRef;
  @ViewChild('inpYear') inpYear: ElementRef;

  year;
  month;
  day;
  errors = inputMgsErrs.dateText;
  private _focus = true;

  constructor(
    @Optional() @Host() @SkipSelf()
    private cc: ControlContainer,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    super(cc);
  }

  get isValDay(): boolean {
    return this.checkValue(this.day) && this.day !== '';
  }

  get isValMonth(): boolean {
    return this.checkValue(this.month) && this.month !== '';
  }

  get isValYear(): boolean {
    return this.checkValue(this.year) && this.year !== '';
  }

  get isValueDate(): boolean {
    return this.isShowDateDiff;
  }

  get isBlur(): boolean {
    return !this._focus;
  }

  get isInvalid() {
    if (this.control) {
      return this.isBlur && (this.submitted || this.control.touched) && this.control.invalid;
    }
    return false;
  }

  get isShowDateDiff(): boolean {
    return this.isValDay && (this.day as string).length === 2
      && this.isValMonth && (this.month as string).length === 2
      && this.isValYear && (this.year as string).length === 4;
  }

  ngOnInit() {
    this.checkInitialControl();
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      this.blurDay();
      this.blurMonth();
      this.blurYear();
    });
  }

  onChangeValueDay(val) {
    this.value = {
      year: this.year,
      month: this.month,
      day: this.checkValue(val) ? this.lPad(val, 2, '0') : null
    };
  }

  onChangeValueMonth(val) {
    this.value = {
      year: this.year,
      month: this.checkValue(val) ? this.lPad(val, 2, '0') : null,
      day: this.day
    };
  }

  onChangeValueYear(val) {
    this.value = {
      year: this.checkValue(val) ? this.lPad(val, 4, '0') : null,
      month: this.month,
      day: this.day
    };
  }

  keyupDay(event) {
    if (this.isValDay && ((this.day as string).length === this.inpDay.nativeElement.maxLength || +event.key > 3)) {
      this.inpMonth.nativeElement.focus();
    }
    this.setAge();
  }

  keyupMonth(event) {
    if (this.isValMonth && ((this.month as string).length === this.inpMonth.nativeElement.maxLength || +event.key > 1)) {
      this.inpYear.nativeElement.focus();
    }
    this.setAge();
  }

  keyupYear(event) {
    if (this.isValYear && ((this.year as string).length === this.inpMonth.nativeElement.maxLength)) {
      // todo statement
    }
    this.setAge();
  }

  blurDay() {
    this._focus = false;
    if (this.isValDay) {
      this.day = this.checkValue(this.day) ? this.lPad(this.day, 2, '0') : null;
    }
  }

  blurMonth() {
    this._focus = false;
    if (this.isValMonth) {
      this.month = this.checkValue(this.month) ? this.lPad(this.month, 2, '0') : null;
    }
  }

  blurYear() {
    this._focus = false;
    if (this.isValYear) {
      this.year = this.checkValue(this.year) ? this.lPad(this.year, 4, '0') : null;
    }
  }

  onfocus() {
    this._focus = true;
  }


  protected setValue(val: DateModel) {
    if (val != null) {
      if(val.year == "" ||val.year == null || val.month == "" ||val.month == null ||val.day == "" ||val.day == null){
        val = null
      }else{
      this.day = val.day;
      this.month = val.month;
      this.year = val.year;
      if (!this._focus) {
        this.day = val.day = this.checkValue(val.day) ? this.lPad(val.day, 2, '0') : null;
        this.month = val.month = this.checkValue(val.month) ? this.lPad(val.month, 2, '0') : null;
        this.year = val.year = this.checkValue(val.year) ? this.lPad(val.year, 4, '0') : null;
      }
      }
    } else {  
      val = { day: null, month: null, year: null };
      this.day = '';
      this.month = '';
      this.year = '';
    }
    super.setValue(val);
    this.setAge();
  }

  private setAge() {
    if (this.isShowDateDiff) {
      if ((this.year as string) !== '0000') {
        const age = this.utilityService.dateDiffCurrentDate(this.value, 'obj');
        this.dateChanged.emit({ year: this.year, month: this.month, day: this.day, age: age });
      }
    }
  }

  private lPad(str, size: number, key: any): string {
    let s = str + '';
    while (s.length < size) {
      s = key + s;
    }
    return s;
  }

  private checkValue(val): boolean {
    return val != null && val !== 'null' && val !== '';
  }


}
